import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFileHref } from "./AppFileHref";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";

// AppTranslate - TRANSLATION'S SHARED CONTRACT (owner interview 2026-09-27, `documentation/design/translation.md`:
// "see a chat totally in English but the messages being sent actually being in Bahasa Indonesia").
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`, NO `node:` import. The SERVICE (`TranslateRules`, `TranslateRun`,
// `TranslateOut`, `TranslateStoreFile`, `DossierHtml`) and the PAGE both read and write through THIS file, so the two
// halves can never disagree about a field (`TranslateContract.test.ts`). A transport here would pass under tsx and
// fail under tsc (the `AppConnect` trap).
//
// ==================================================================================================
// !! THE FIVE PIECES OF THE CONTRACT
// ==================================================================================================
//
// 1. SETTINGS - `translate.json` in the person's folder in the subscriber's own Dropbox, beside `facts.json`/
//    `tasks.json`. HUMAN STATE, written by the service only on `/translate {action:"set"}` with `PersonFileEdit`'s
//    discipline (hold shared with the push, Dropbox FIRST, a missing local file read from Dropbox first).
//      { "schema": 1, "personKey": "+62...", "myLang": "en", "theirLang": "id", "on": true,
//        "glossary": [ { "term": "Putri", "rule": "keep" }, { "term": "villa", "rule": "as", "as": "vila" } ],
//        "at": ISO-8601 UTC (last saved) }
//    BOTH languages are per contact (no account-wide default). Translation is OFF when the two languages are equal
//    (`Active`: the base subtags compared, so `en-GB` and `en-US` are one language).
//    ⛔ STRICT (`AppAiChat`'s rule): one malformed field makes the whole file `Unreadable` - fate 1: nothing is
//    translated for that contact, one ink sentence, and the service NEVER writes over it.
//
// 2. TRANSLATIONS - `translations.json` in the same folder: message id -> { text, needed, lang, model, at, dir }.
//    DERIVED (it cost money, so it is kept and pushed like `facts.json`; `FolderRestore` brings it back). Written by
//    the service only (the cycle's batch, `/translate {action:"earlier"}`, the live feed, and the outbox for what
//    the owner SENT - `dir: "out"`, `text` = what the owner typed, the journal holds what went out).
//    `needed: false` is kept too (text "") so a message already in my language is never asked about again.
//    ⚠ LENIENT per entry (a bad entry is skipped - nothing human is lost), a file that is not JSON reads as empty
//    and `Unreadable` (the service then starts it again: re-translating is money, never lost work). ⛔ CAP:
//    the newest `MaxEntries` by `at`, and at most `MaxEntryText` characters per text.
//
// 3. `/translate` - one POST per question, every subscriber's own `browser` key (`ArchiveKey.BrowserMatch`), the
//    page resolved through the tenant's own `index.json`, 1:1 only. `action`:
//      - "get"     -> `{ok, available, why, settings|null, suggested|null, capped, unreadable}`
//      - "set"     -> `{ok, saved, settings}` (body carries `settings`: myLang, theirLang, on, glossary)
//      - "earlier" -> `{ok, items:[{id, text, needed}], more}` - the next `BatchMax` untranslated messages OLDER
//                     than `before` ("" = the newest - the backfill after switching on); `items` are also saved.
//    ⭐ `available: false` = the chip is hidden (no AI route for this subscriber: not the owner and no metered key).
//    ⛔ DORMANT: with `WAC_TASKS`-style switch `WAC_TRANSLATE` not exactly "1" the route does not exist and answers
//    the status document (no boolean `ok`) - `Parse` then says `Off`, and the page shows no chip at all.
//
// 4. SENDING - `/send`'s request carries `translate: true` when the chip is on for that chat. ⛔ THE PAGE SENDS
//    ONLY THE OWNER'S TYPED TEXT AND THAT FLAG; THE SERVICE DECIDES THE OUTGOING TEXT (translated, or the typed bytes
//    exactly when they are already in the contact's language). "Send in English" is a SECOND `/send` without the
//    flag (a new human click, a new id). History rows add `sentText` (what went out when it differs from what the
//    owner typed, else "") and the state `translate-failed` (NOTHING was sent - "Not sent - couldn't translate",
//    with Retry / Send in English).
//
// 5. LIVE (`/live`) - items already carry `text` (my language), `original` (what was written, when translated) and
//    `needed` (`AppLiveRules.Shown` / `OriginalOf` read them).
//
// ⭐ THE PAGE MARKUP (`BubbleClass`, `OriginalClass`, `FlagClass`, `CopyClass`) and `Css` are ONE source for the
// archive page (`DossierHtml`) and the app's frame sheet; the flag is `AppIcons.Flag`. The archive page carries a
// small inline script for standalone use, marked `data-wac-standalone` - the app removes exactly that script with
// `AppPageScript.Strip` and attaches its own code from outside (the app's CSP allows no inline script).
// ⚠ PRIVATE DATA: settings name languages and words; translations are message text. Never logged.

/** One glossary line: keep a word as written, or always translate it one way. */
export class AppTrGlossaryItem {
    public Term: string = "";
    /** `keep` or `as`. */
    public Rule: string = "";
    /** For `as`: what the term always becomes. "" for `keep`. */
    public As: string = "";
}

/** One contact's translation settings. */
export class AppTrSettings {
    /** The file was there. */
    public Found: boolean = false;
    /** The file was there and could not be read - never written over. */
    public Unreadable: boolean = false;
    public PersonKey: string = "";
    /** The OWNER's language for this chat (BCP-47, e.g. "en"). */
    public MyLang: string = "";
    /** The CONTACT's language for this chat (BCP-47, e.g. "id"). */
    public TheirLang: string = "";
    public On: boolean = false;
    public Glossary: AppTrGlossaryItem[] = [];
    /** When the owner last saved them (ISO-8601 UTC). */
    public At: string = "";
}

/** One message's translation, as `translations.json` keeps it. */
export class AppTrEntry {
    /** The translation - my language for `in`, what the owner TYPED for `out`. "" when not needed. */
    public Text: string = "";
    /** False: the message was already in the target language (shown exactly as written, no flag). */
    public Needed: boolean = false;
    /** The language `Text` is in (BCP-47). */
    public Lang: string = "";
    /** The model that decided ("" for `out`, which records what the owner typed). */
    public Model: string = "";
    /** When it was translated (ISO-8601 UTC). */
    public At: string = "";
    /** `in` (their message, shown in my language) or `out` (mine, sent in theirs). */
    public Dir: string = "";
    /**
     * ⭐ WHICH WORDS WERE TRANSLATED (2026-10-01, edits): `AppTranslate.SrcOf` of the message's text when the entry
     * was made (`src` in the file, written only when set - an older entry has none). A message can be EDITED after it
     * was translated; `AppTranslate.Fresh` then refuses the entry and the new words are translated instead.
     */
    public Src: string = "";
}

/** One contact's translations, read. */
export class AppTrStore {
    public Found: boolean = false;
    /** The file was there and was not JSON - the service starts it again (derived data). */
    public Unreadable: boolean = false;
    public PersonKey: string = "";
    /** Message id -> its translation. */
    public Items: Record<string, AppTrEntry> = {};
}

/** The service's suggestion: "Mirliyana writes in Bahasa Indonesia - translate this chat?". */
export class AppTrSuggestion {
    public MyLang: string = "";
    public TheirLang: string = "";
}

/** One translated message in an `earlier` answer (the page puts it on the bubble with that `data-mid`). */
export class AppTrItem {
    public Id: string = "";
    public Text: string = "";
    public Needed: boolean = false;
}

/** One `/translate` answer, read. */
export class AppTrAnswer {
    /** The route is real and said yes. */
    public Ok: boolean = false;
    /** ⛔ The route does not exist here (dormant or older service) - no chip, never ask again this visit. */
    public Off: boolean = false;
    /** The sentence to show as-is when `Ok` is false (or when `Available` is false). */
    public Why: string = "";
    /** Translation can run for this subscriber (the owner's plan, or a metered key). False = hide the chip. */
    public Available: boolean = false;
    public Settings: AppTrSettings | null = null;
    public Suggested: AppTrSuggestion | null = null;
    /** This month's AI allowance is used up - `CappedWhy` is shown, incoming stays untranslated. */
    public Capped: boolean = false;
    /** The settings file could not be read (never written over). */
    public Unreadable: boolean = false;
    /** `set`: the settings are in Dropbox. */
    public Saved: boolean = false;
    /** `earlier`: what was translated (and saved). */
    public Items: AppTrItem[] = [];
    /** `earlier`: there are still older untranslated messages. */
    public More: boolean = false;
    /**
     * ⭐ A refusal that clears on its own (`{ok:false, why, retry}` - the service restarting, its folder not back yet):
     * seconds before asking again (1-600), 0 = a final answer. ⛔ Such an answer is NOT remembered for the visit.
     */
    public RetrySec: number = 0;
}

/** Orders store ids oldest first by `at` - a class, not an inline comparator (house rule). */
export class AppTrOrder {
    private Store: AppTrStore;

    public constructor(Store: AppTrStore) {
        this.Store = Store;
    }

    /** DOCUMENTED EXCEPTION: comparator for `Array.sort`. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public Oldest = (A: string, B: string): number => {
        if (this.Store.Items[A].At < this.Store.Items[B].At) { return -1; }
        if (this.Store.Items[A].At > this.Store.Items[B].At) { return 1; }
        if (A < B) { return -1; }
        return 1;
    };
}

export class AppTranslate {
    public static readonly Route: string = "/translate";
    public static readonly SettingsName: string = "translate.json";
    public static readonly StoreName: string = "translations.json";
    public static readonly Schema: number = 1;
    public static readonly Actions: string[] = ["get", "set", "earlier"];

    /** `/send`'s request field (the page sets it to true when the chip is on for that chat). */
    public static readonly SendField: string = "translate";
    /** A history row's text that went out when it differs from what the owner typed. */
    public static readonly SentTextField: string = "sentText";
    /** A history row's state when the message could not be translated - NOTHING was sent. */
    public static readonly FailedState: string = "translate-failed";

    /** How many messages one `earlier` (and the switch-on backfill) translates. */
    public static readonly BatchMax: number = 200;
    /** Glossary lines per contact, and the longest term / replacement. */
    public static readonly MaxGlossary: number = 50;
    public static readonly MaxTerm: number = 60;
    /** ⛔ THE STORE CAP: the newest entries by `at`, and the longest text one entry may carry. */
    public static readonly MaxEntries: number = 4000;
    public static readonly MaxEntryText: number = 8192;

    /** The quiet line at the monthly cap (owner's words, the design note). */
    public static readonly CappedWhy: string = "This month's AI allowance is used up - translation resumes on the 1st.";
    /** The bubble's words when a translated send could not be translated (nothing was sent). */
    public static readonly FailedLine: string = "Not sent - couldn't translate";

    // ── The page markup (one source for the archive page and the app's frame) ─────────────────
    /** On a bubble whose text is a translation. */
    public static readonly BubbleClass: string = "tr";
    /** The hidden original + Copy, inside the bubble. */
    public static readonly OriginalClass: string = "tro";
    /** The original's own text span. */
    public static readonly OriginalTextClass: string = "trt";
    /** The flag button that shows/hides the original. */
    public static readonly FlagClass: string = "trf";
    /** The Copy button inside the original. */
    public static readonly CopyClass: string = "trc";
    /** On a bubble whose original is shown (the flag was pressed). */
    public static readonly OpenClass: string = "tron";
    public static readonly FlagLabel: string = "Show the original";
    public static readonly FlagLabelOut: string = "Show what was sent";
    public static readonly CopyLabel: string = "Copy";

    /**
     * The languages the chip offers, `[code, name]`, sorted by name. ⚠ Any well-formed BCP-47 tag is accepted
     * (`LangOk`); this list is only what the picker shows and how a code is named to a person and to the model.
     */
    public static readonly Languages: string[][] = [
        ["ar", "Arabic"], ["id", "Bahasa Indonesia"], ["zh", "Chinese"], ["da", "Danish"], ["nl", "Dutch"], ["en", "English"],
        ["tl", "Filipino"], ["fi", "Finnish"], ["fr", "French"], ["de", "German"], ["el", "Greek"],
        ["he", "Hebrew"], ["hi", "Hindi"], ["it", "Italian"], ["ja", "Japanese"],
        ["ko", "Korean"], ["ms", "Malay"], ["nb", "Norwegian"], ["pl", "Polish"], ["pt", "Portuguese"],
        ["ru", "Russian"], ["es", "Spanish"], ["sv", "Swedish"], ["th", "Thai"], ["tr", "Turkish"],
        ["uk", "Ukrainian"], ["vi", "Vietnamese"],
    ];

    /**
     * A well-formed language tag (`en`, `pt-BR`, `zh-Hant`). DOCUMENTED EXCEPTION: pure predicate.
     * ERROR-SUPPRESSED-JUSTIFIED: false IS the answer for anything else.
     */
    public static LangOk = (Code: unknown): boolean => {
        if (typeof Code !== "string") { return false; }
        return /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/.test(Code);
    };

    /** The primary subtag, lower case ("pt-BR" -> "pt"). DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Base = (Code: string): string => {
        return nz(Code).split("-")[0].toLowerCase();
    };

    /** "Bahasa Indonesia" for "id"; the code itself when the list does not name it. */
    public static NameOf = (Code: string): string => {
        if (getCoreUtils().KErrorMode === true) { return nz(Code); }
        try {
            for (const One of AppTranslate.Languages) {
                if (One[0] === nz(Code)) { return One[1]; }
            }
            for (const One of AppTranslate.Languages) {
                if (One[0] === AppTranslate.Base(Code)) { return One[1]; }
            }
            return nz(Code);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.NameOf");
            return nz(Code);
        }
    };

    /** Two tags naming the same language (their base subtags agree). */
    public static SameLanguage = (A: string, B: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            return AppTranslate.Base(A).length > 0 && AppTranslate.Base(A) === AppTranslate.Base(B);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.SameLanguage");
            return true;
        }
    };

    /** ⭐ TRANSLATION RUNS FOR THIS CHAT: readable, on, both languages set, and DIFFERENT. */
    public static Active = (S: AppTrSettings | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (S === null || S.Unreadable === true || S.On !== true) { return false; }
            if (AppTranslate.LangOk(S.MyLang) === false || AppTranslate.LangOk(S.TheirLang) === false) { return false; }
            return AppTranslate.SameLanguage(S.MyLang, S.TheirLang) === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.Active");
            return false;
        }
    };

    /** `People/Tomo/<Name>` from a page href `People/Tomo/Tomo.html`, or "" for an href with no folder. */
    public static FileOf = (Href: string, Name: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
                return AppFileHref.FileOf(Href, Name);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.FileOf");
            return "";
        }
    };

    // ── 1. Settings ──────────────────────────────────────────────────────────────────────────

    /** The settings file's text as settings. ⛔ STRICT - see the header. Absent text is the caller's question. */
    public static ParseSettings = (Text: string): AppTrSettings => {
        if (getCoreUtils().KErrorMode === true) { return AppTranslate.BadSettings(); }
        try {
            const Doc: Record<string, unknown> | null = AppTranslate.ObjectIn(Text);
            if (Doc === null || Number(Doc.schema) !== AppTranslate.Schema) { return AppTranslate.BadSettings(); }
            if (Doc.personKey !== undefined && typeof Doc.personKey !== "string") { return AppTranslate.BadSettings(); }
            if (Doc.at !== undefined && typeof Doc.at !== "string") { return AppTranslate.BadSettings(); }
            const Out: AppTrSettings | null = AppTranslate.SettingsIn(Doc);
            if (Out === null) { return AppTranslate.BadSettings(); }
            Out.Found = true;
            Out.PersonKey = nz(Doc.personKey as string);
            Out.At = nz(Doc.at as string);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.ParseSettings");
            return AppTranslate.BadSettings();
        }
    };

    /**
     * `myLang`, `theirLang`, `on` and `glossary` of an object - from the file or from a `set` request - or null when
     * any is not exactly the shape this build writes. ⚠ An empty language is allowed (not chosen yet).
     */
    public static SettingsIn = (Doc: Record<string, unknown>): AppTrSettings | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (typeof Doc.myLang !== "string" || typeof Doc.theirLang !== "string" || typeof Doc.on !== "boolean") { return null; }
            if ((Doc.myLang as string).length > 0 && AppTranslate.LangOk(Doc.myLang) === false) { return null; }
            if ((Doc.theirLang as string).length > 0 && AppTranslate.LangOk(Doc.theirLang) === false) { return null; }
            if (Array.isArray(Doc.glossary) === false || (Doc.glossary as unknown[]).length > AppTranslate.MaxGlossary) { return null; }
            const Out: AppTrSettings = new AppTrSettings();
            Out.MyLang = Doc.myLang as string;
            Out.TheirLang = Doc.theirLang as string;
            Out.On = Doc.on as boolean;
            for (const Raw of Doc.glossary as unknown[]) {
                const One: AppTrGlossaryItem | null = AppTranslate.GlossaryIn(Raw);
                if (One === null) { return null; }
                Out.Glossary.push(One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.SettingsIn");
            return null;
        }
    };

    /** One glossary line, or null. DOCUMENTED EXCEPTION: pure shape check. ERROR-SUPPRESSED-JUSTIFIED: null IS the answer. */
    private static GlossaryIn = (Raw: unknown): AppTrGlossaryItem | null => {
        if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
        const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
        if (typeof Bag.term !== "string" || Bag.term.trim().length === 0 || Bag.term.length > AppTranslate.MaxTerm) { return null; }
        if (Bag.rule !== "keep" && Bag.rule !== "as") { return null; }
        const Out: AppTrGlossaryItem = new AppTrGlossaryItem();
        Out.Term = Bag.term;
        Out.Rule = Bag.rule as string;
        if (Out.Rule === "keep" && Bag.as === undefined) { return Out; }
        if (Out.Rule === "keep") { return null; }
        if (typeof Bag.as !== "string" || Bag.as.trim().length === 0 || Bag.as.length > AppTranslate.MaxTerm) { return null; }
        Out.As = Bag.as;
        return Out;
    };

    /** The settings as the file's text - the ONE serialiser. */
    public static SettingsJson = (S: AppTrSettings): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: Record<string, unknown> = AppTranslate.SettingsWire(S);
            Out.at = nz(S.At);
            return JSON.stringify(Object.assign({ schema: AppTranslate.Schema, personKey: nz(S.PersonKey) }, Out), null, 1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.SettingsJson");
            return "";
        }
    };

    /** `{myLang, theirLang, on, glossary}` - the file's fields, a `set` body's `settings`, and the answers'. */
    public static SettingsWire = (S: AppTrSettings): Record<string, unknown> => {
        if (getCoreUtils().KErrorMode === true) { return {}; }
        try {
            const Glossary: Record<string, unknown>[] = [];
            for (const One of S.Glossary) {
                const Line: Record<string, unknown> = { term: One.Term, rule: One.Rule };
                if (One.Rule === "as") { Line.as = One.As; }
                Glossary.push(Line);
            }
            return { myLang: nz(S.MyLang), theirLang: nz(S.TheirLang), on: S.On === true, glossary: Glossary };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.SettingsWire");
            return {};
        }
    };

    /** For the model: `keep "Putri" as is; always translate "villa" as "vila"`, or "none". */
    public static GlossaryLine = (Glossary: AppTrGlossaryItem[]): string => {
        if (getCoreUtils().KErrorMode === true) { return "none"; }
        try {
            const Parts: string[] = [];
            for (const One of Glossary) {
                if (One.Rule === "keep") { Parts.push("keep " + JSON.stringify(One.Term) + " as is"); }
                if (One.Rule === "as") { Parts.push("always translate " + JSON.stringify(One.Term) + " as " + JSON.stringify(One.As)); }
            }
            if (Parts.length === 0) { return "none"; }
            return Parts.join("; ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.GlossaryLine");
            return "none";
        }
    };

    // ── 2. Translations ──────────────────────────────────────────────────────────────────────

    /** The store's text. ⚠ LENIENT per entry - see the header. */
    public static ParseStore = (Text: string): AppTrStore => {
        const Out: AppTrStore = new AppTrStore();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (nz(Text).trim().length === 0) { return Out; }
            Out.Found = true;
            const Doc: Record<string, unknown> | null = AppTranslate.ObjectIn(Text);
            if (Doc === null || Number(Doc.schema) !== AppTranslate.Schema || Doc.items === null
                || typeof Doc.items !== "object" || Array.isArray(Doc.items) === true) {
                Out.Unreadable = true;
                return Out;
            }
            Out.PersonKey = AppTranslate.StringOr(Doc.personKey);
            const Items: Record<string, unknown> = Doc.items as Record<string, unknown>;
            for (const Id of Object.keys(Items)) {
                const One: AppTrEntry | null = AppTranslate.EntryIn(Id, Items[Id]);
                if (One !== null) { Out.Items[Id] = One; }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.ParseStore");
            return Out;
        }
    };

    /** One entry, or null. DOCUMENTED EXCEPTION: pure shape check. ERROR-SUPPRESSED-JUSTIFIED: null IS the answer. */
    private static EntryIn = (Id: string, Raw: unknown): AppTrEntry | null => {
        if (AppTranslate.MessageIdOk(Id) === false) { return null; }
        if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
        const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
        if (typeof Bag.text !== "string" || Bag.text.length > AppTranslate.MaxEntryText || typeof Bag.needed !== "boolean") { return null; }
        if (typeof Bag.at !== "string" || isNaN(Date.parse(Bag.at)) === true) { return null; }
        if (Bag.dir !== "in" && Bag.dir !== "out") { return null; }
        if (Bag.needed === true && Bag.text.trim().length === 0) { return null; }
        const Out: AppTrEntry = new AppTrEntry();
        if (Bag.needed === true) { Out.Text = Bag.text; }
        Out.Needed = Bag.needed;
        Out.Lang = AppTranslate.StringOr(Bag.lang);
        Out.Model = AppTranslate.StringOr(Bag.model);
        Out.At = Bag.at;
        Out.Dir = Bag.dir as string;
        if (typeof Bag.src === "string" && /^[0-9a-f]{8}$/.test(Bag.src) === true) { Out.Src = Bag.src; }
        return Out;
    };

    /**
     * ⭐ A short fingerprint of a message's words (FNV-1a, 8 hex) - what an entry's `src` holds. Not a secret and not
     * the words: it only answers "is this the text that was translated?". DOCUMENTED EXCEPTION: pure arithmetic.
     * ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static SrcOf = (Text: string): string => {
        const Words: string = nz(Text);
        let Acc: number = 0x811c9dc5;
        for (let Pos: number = 0; Pos < Words.length; Pos = Pos + 1) { Acc = Math.imul(Acc ^ Words.charCodeAt(Pos), 0x01000193) >>> 0; }
        return ("00000000" + Acc.toString(16)).slice(-8);
    };

    /**
     * ⛔ IS THIS ENTRY STILL ABOUT THE MESSAGE AS IT READS NOW? An entry that says which words it translated (`Src`)
     * is fresh exactly when those are the words; one that does not (made before 2026-10-01, or an `out` entry - what
     * the owner typed) is fresh unless the message was EDITED since. A stale entry is treated as no entry: the bubble
     * shows the new words as written and the next translation pass asks about them.
     * DOCUMENTED EXCEPTION: pure comparison. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static Fresh = (Entry: AppTrEntry | null | undefined, Text: string, Edited: boolean): boolean => {
        return AppTranslate.FreshSrc(Entry, AppTranslate.SrcOf(Text), Edited);
    };

    /** `Fresh`, for a caller that kept the fingerprint instead of the words (the search index).
     *  DOCUMENTED EXCEPTION: pure comparison. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static FreshSrc = (Entry: AppTrEntry | null | undefined, Src: string, Edited: boolean): boolean => {
        if (Entry === null || Entry === undefined) { return false; }
        if (nz(Entry.Src).length > 0) { return Entry.Src === Src; }
        return Edited !== true;
    };

    /** A message id as a bubble's `data-mid` carries it (`LiveRules.Drawable`'s rule). DOCUMENTED EXCEPTION: pure.
     *  ERROR-SUPPRESSED-JUSTIFIED: false IS the answer for anything else. */
    public static MessageIdOk = (Id: unknown): boolean => {
        if (typeof Id !== "string") { return false; }
        return /^[A-Za-z0-9_-]{1,64}$/.test(Id);
    };

    /** Put (or replace) one entry, then apply the cap: the newest `MaxEntries` by `at`. */
    public static Put = (Store: AppTrStore, Id: string, Entry: AppTrEntry): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTranslate.MessageIdOk(Id) === false) { return; }
            if (Entry.Needed === true && (Entry.Text.trim().length === 0 || Entry.Text.length > AppTranslate.MaxEntryText)) { return; }
            if (Entry.Needed === false) { Entry.Text = ""; }
            Store.Items[Id] = Entry;
            const Ids: string[] = Object.keys(Store.Items);
            if (Ids.length <= AppTranslate.MaxEntries) { return; }
            const Order: AppTrOrder = new AppTrOrder(Store);
            Ids.sort(Order.Oldest);
            for (const Old of Ids.slice(0, Ids.length - AppTranslate.MaxEntries)) { delete Store.Items[Old]; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.Put");
        }
    };

    /** The store as the file's text - the ONE serialiser (ids sorted, so an unchanged store writes the same bytes). */
    public static StoreJson = (Store: AppTrStore): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Items: Record<string, unknown> = {};
            for (const Id of Object.keys(Store.Items).sort()) {
                const One: AppTrEntry = Store.Items[Id];
                const Line: Record<string, unknown> = { text: One.Text, needed: One.Needed, lang: One.Lang,
                    at: One.At, dir: One.Dir };
                if (One.Model.length > 0) { Line.model = One.Model; }
                if (nz(One.Src).length > 0) { Line.src = One.Src; }
                Items[Id] = Line;
            }
            return JSON.stringify({ schema: AppTranslate.Schema, personKey: nz(Store.PersonKey), items: Items });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.StoreJson");
            return "";
        }
    };

    /** The entry that changes what a bubble says (needed, with text), or null. */
    public static Shown = (Store: AppTrStore | null, Id: string): AppTrEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Store === null || AppTranslate.MessageIdOk(Id) === false) { return null; }
            const One: AppTrEntry | undefined = Store.Items[Id];
            if (One === undefined || One.Needed !== true || One.Text.length === 0) { return null; }
            return One;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.Shown");
            return null;
        }
    };

    /**
     * `Shown`, for a message whose words are in hand: the entry only while it is `Fresh` for those words (an edited
     * message never shows the translation of what it used to say).
     */
    public static ShownFor = (Store: AppTrStore | null, Id: string, Text: string, Edited: boolean): AppTrEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const One: AppTrEntry | null = AppTranslate.Shown(Store, Id);
            if (One === null || AppTranslate.Fresh(One, Text, Edited) === false) { return null; }
            return One;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.ShownFor");
            return null;
        }
    };

    // ── 3. /translate - the page's bodies and the reading of the answer ───────────────────────

    /** The body of one `/translate` call. `Settings` only for `set`; `Before` only for `earlier`. */
    public static BodyFor = (Number: string, Key: string, Page: string, Action: string,
                             Settings: AppTrSettings | null, Before: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: Record<string, unknown> = { number: nz(Number), key: nz(Key), page: nz(Page), action: nz(Action) };
            if (Action === "set" && Settings !== null) { Out.settings = AppTranslate.SettingsWire(Settings); }
            if (Action === "earlier") { Out.before = nz(Before); }
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.BodyFor");
            return "";
        }
    };

    /** The longest wait a `retry` may ask for. */
    public static readonly MaxRetrySec: number = 600;

    /** What `/translate` answered. ⛔ No boolean `ok` (the status document of a dormant service) = `Off`. */
    public static Parse = (Text: string): AppTrAnswer => {
        const Out: AppTrAnswer = new AppTrAnswer();
        if (getCoreUtils().KErrorMode === true) { Out.Off = true; return Out; }
        try {
            const Doc: Record<string, unknown> | null = AppTranslate.ObjectIn(Text);
            if (Doc === null || typeof Doc.ok !== "boolean") { Out.Off = true; return Out; }
            Out.Ok = Doc.ok === true;
            Out.Why = AppTranslate.StringOr(Doc.why);
            Out.Available = Doc.available === true;
            Out.Capped = Doc.capped === true;
            Out.Unreadable = Doc.unreadable === true;
            Out.Saved = Doc.saved === true;
            Out.More = Doc.more === true;
            if (Out.Ok !== true && typeof Doc.retry === "number" && Number.isFinite(Doc.retry) === true && Doc.retry > 0) {
                Out.RetrySec = Math.min(AppTranslate.MaxRetrySec, Math.ceil(Doc.retry));
            }
            if (Doc.settings !== null && typeof Doc.settings === "object" && Array.isArray(Doc.settings) === false) {
                Out.Settings = AppTranslate.SettingsIn(Doc.settings as Record<string, unknown>);
            }
            Out.Suggested = AppTranslate.SuggestionIn(Doc.suggested);
            Out.Items = AppTranslate.ItemsIn(Doc.items);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslate.Parse");
            Out.Off = true;
            return Out;
        }
    };

    /** DOCUMENTED EXCEPTION: pure shape check. ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for anything else. */
    private static SuggestionIn = (Raw: unknown): AppTrSuggestion | null => {
        if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
        const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
        if (AppTranslate.LangOk(Bag.myLang) === false || AppTranslate.LangOk(Bag.theirLang) === false) { return null; }
        const Out: AppTrSuggestion = new AppTrSuggestion();
        Out.MyLang = Bag.myLang as string;
        Out.TheirLang = Bag.theirLang as string;
        return Out;
    };

    /** DOCUMENTED EXCEPTION: pure shape check. ERROR-SUPPRESSED-JUSTIFIED: a malformed item is skipped. */
    private static ItemsIn = (Raw: unknown): AppTrItem[] => {
        const Out: AppTrItem[] = [];
        if (Array.isArray(Raw) === false) { return Out; }
        for (const One of Raw as unknown[]) {
            if (One === null || typeof One !== "object") { continue; }
            const Bag: Record<string, unknown> = One as Record<string, unknown>;
            if (AppTranslate.MessageIdOk(Bag.id) === false || typeof Bag.text !== "string" || typeof Bag.needed !== "boolean") { continue; }
            const Item: AppTrItem = new AppTrItem();
            Item.Id = Bag.id as string;
            Item.Text = Bag.text;
            Item.Needed = Bag.needed;
            Out.push(Item);
        }
        return Out;
    };

    /** The wire form of an `earlier` item (the service's answer). */
    public static ItemWire = (Id: string, Entry: AppTrEntry): Record<string, unknown> => {
        return { id: Id, text: Entry.Text, needed: Entry.Needed === true };
    };

    // ── The page's look (one source: the archive page's <style> and the app's frame sheet) ─────

    /**
     * The CSS for translated bubbles. ⛔ CLASSES ONLY - no inline `style` anywhere (the app's CSP). The original is
     * hidden until the bubble carries `OpenClass` (the flag's toggle); the flag sits beside the clock.
     */
    public static Css = (): string => {
        return ".b.tr{min-width:92px}"
            + ".b.tr .trf{display:inline-flex;align-items:center;justify-content:center;width:22px;height:18px;"
            + "position:absolute;right:44px;bottom:3px;margin:0;padding:0;border:0;background:transparent;color:inherit;"
            + "opacity:.55;cursor:pointer}"
            + ".b.tr .trf:hover,.b.tr .trf:focus-visible{opacity:1}"
            + ".b.tr .trf svg{width:15px;height:15px}"
            + ".b.tr .tro{display:none;margin:6px 0 2px;padding:6px 8px;border-left:3px solid currentColor;"
            + "border-radius:4px;opacity:.85;font-size:.94em}"
            + ".b.tr.tron .tro{display:block}"
            + ".b.tr .trt{display:block;white-space:pre-wrap;overflow-wrap:anywhere}"
            + ".b.tr .trc{margin-top:4px;padding:1px 8px;border:1px solid currentColor;border-radius:4px;"
            + "background:transparent;color:inherit;font:inherit;font-size:.85em;cursor:pointer}";
    };

    // ── Helpers ──────────────────────────────────────────────────────────────────────────────

    /**
     * DOCUMENTED EXCEPTION: pure parse probe over text in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for text that is not a JSON object.
     */
    public static ObjectIn = (Text: string): Record<string, unknown> | null => {
        return kJsonObject.ObjectOfJson(Text);
    };

    /** A string, or "" for anything else. DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: "" IS the answer. */
    public static StringOr = (Value: unknown): string => {
        if (typeof Value !== "string") { return ""; }
        return Value;
    };

    /** DOCUMENTED EXCEPTION: constant safe-default factory - the unreadable settings. */
    private static BadSettings = (): AppTrSettings => {
        const Out: AppTrSettings = new AppTrSettings();
        Out.Found = true;
        Out.Unreadable = true;
        return Out;
    };
}
